/**
 * AlertCenterPage — 告警与审批中心
 *
 * Sections:
 * 1. Active Alerts — severity color-coded, acknowledge/resolve actions
 * 2. Token Budget — current usage vs budget, limits
 * 3. Approval Queue — pending human approvals
 *
 * Data from:
 * - GET /api/v1/devops/alerts
 * - POST /api/v1/devops/alerts/:id/acknowledge
 * - POST /api/v1/devops/alerts/:id/resolve
 * - GET /api/v1/token-budgets
 * - GET /api/v1/goals?status=pending_approval (approval queue)
 */

import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Bell,
  RefreshCw,
  Loader2,
  AlertTriangle,
  AlertOctagon,
  Info,
  CheckCircle2,
  XCircle,
  DollarSign,
  ShieldAlert,
  ThumbsUp,
  Clock,
  Coins,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ─────────────────────────────────────

interface AlertItem {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  source: string;
  created_at: string;
  acknowledged: boolean;
  resolved: boolean;
}

interface TokenBudget {
  id: string;
  tenant_id: string;
  budget_usd: number;
  used_usd: number;
  period: string;
  reset_at: string;
}

interface ApprovalItem {
  id: string;
  title: string;
  skill_id: string;
  risk_class: string;
  status: string;
  created_at: string;
}

// ─── Page Component ────────────────────────────

export default function AlertCenterPage() {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [budgets, setBudgets] = useState<TokenBudget[]>([]);
  const [approvals, setApprovals] = useState<ApprovalItem[]>([]);
  const [processing, setProcessing] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [alertRes, budgetRes, approvalRes] = await Promise.allSettled([
      api.get<{ items: AlertItem[] }>('/devops/alerts?limit=50'),
      api.get<{ items: TokenBudget[] }>('/token-budgets'),
      api.get<{ items: ApprovalItem[] }>('/goals?status=pending_approval&limit=20'),
    ]);

    if (alertRes.status === 'fulfilled') setAlerts(alertRes.value.items ?? []);
    if (budgetRes.status === 'fulfilled') setBudgets(budgetRes.value.items ?? []);
    if (approvalRes.status === 'fulfilled') setApprovals(approvalRes.value.items ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { void fetchData(); }, [fetchData]);

  // Auto-refresh every 30s
  useEffect(() => {
    const timer = setInterval(() => void fetchData(), 30_000);
    return () => clearInterval(timer);
  }, [fetchData]);

  const handleAlertAction = async (id: string, action: 'acknowledge' | 'resolve') => {
    setProcessing((prev) => new Set(prev).add(id));
    try {
      await api.post(`/devops/alerts/${id}/${action}`, {});
      setAlerts((prev) =>
        prev.map((a) =>
          a.id === id
            ? { ...a, acknowledged: action === 'acknowledge' ? true : a.acknowledged, resolved: action === 'resolve' ? true : a.resolved }
            : a,
        ),
      );
    } catch (err) {
      console.error(`Alert ${action} failed:`, err);
    } finally {
      setProcessing((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const activeAlerts = alerts.filter((a) => !a.resolved);
  const criticalCount = activeAlerts.filter((a) => a.severity === 'critical').length;
  const warningCount = activeAlerts.filter((a) => a.severity === 'warning').length;

  return (
    <div className="space-y-6 p-6">
      {/* ─── Header ───────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6 text-red-500" />
            Alert & Approval Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live Alerts · Token Budget · Human Approval Queue
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* ─── Status Bar ───────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MiniCard label="Active Alerts" value={activeAlerts.length} icon={<Bell className="h-4 w-4 text-orange-500" />} />
        <MiniCard label="Critical" value={criticalCount} icon={<AlertOctagon className="h-4 w-4 text-red-600" />} />
        <MiniCard label="Warning" value={warningCount} icon={<AlertTriangle className="h-4 w-4 text-yellow-500" />} />
        <MiniCard label="Pending Approval" value={approvals.length} icon={<ThumbsUp className="h-4 w-4 text-primary" />} />
      </div>

      {/* ─── Token Budgets ────────────────── */}
      {budgets.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1">
              <Coins className="h-4 w-4 text-amber-500" />
              Token Budget
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {budgets.map((b) => {
                const pct = b.budget_usd > 0 ? (b.used_usd / b.budget_usd) * 100 : 0;
                const color = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-yellow-500' : 'bg-green-500';
                return (
                  <div key={b.id}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium">{b.period}</span>
                      <span className="text-muted-foreground">
                        ${b.used_usd.toFixed(2)} / ${b.budget_usd.toFixed(2)} ({pct.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Reset at: {new Date(b.reset_at).toLocaleString('en-SG', { hour12: false })}
                    </p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Active Alerts ────────────────── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-1">
            <ShieldAlert className="h-4 w-4 text-red-500" />
            Active Alerts ({activeAlerts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeAlerts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              <CheckCircle2 className="h-5 w-5 inline mr-1 text-green-500" />
              All clear — no active alerts
            </p>
          ) : (
            <div className="space-y-2">
              {activeAlerts.map((alert) => {
                const isProc = processing.has(alert.id);
                return (
                  <div
                    key={alert.id}
                    className={cn(
                      'flex items-start gap-3 p-3 rounded border',
                      alert.severity === 'critical' && 'border-red-300 bg-red-50 dark:bg-red-950/20',
                      alert.severity === 'warning' && 'border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20',
                      alert.severity === 'info' && 'border-blue-300 bg-blue-50 dark:bg-blue-950/20',
                    )}
                  >
                    {/* Icon */}
                    {alert.severity === 'critical' ? (
                      <AlertOctagon className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                    ) : alert.severity === 'warning' ? (
                      <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                    ) : (
                      <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{alert.message}</p>
                      <p className="text-[10px] text-muted-foreground">
                        Source: {alert.source} · {new Date(alert.created_at).toLocaleString('en-SG', { hour12: false })}
                      </p>
                      {alert.acknowledged && (
                        <Badge variant="outline" className="text-[10px] mt-1">Acknowledged</Badge>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1 shrink-0">
                      {!alert.acknowledged && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          disabled={isProc}
                          onClick={() => void handleAlertAction(alert.id, 'acknowledge')}
                        >
                          {isProc ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Ack'}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={isProc}
                        onClick={() => void handleAlertAction(alert.id, 'resolve')}
                      >
                        {isProc ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Resolve'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Approval Queue ───────────────── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-1">
            <ThumbsUp className="h-4 w-4 text-primary" />
            Human Approval Queue ({approvals.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {approvals.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No pending approvals
            </p>
          ) : (
            <div className="space-y-2">
              {approvals.map((item) => (
                <div key={item.id} className="flex items-center gap-3 p-2 rounded border text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{item.title}</p>
                    <p className="text-[10px] text-muted-foreground">
                      Skill: {item.skill_id} · Risk: {item.risk_class} · {new Date(item.created_at).toLocaleString('en-SG', { hour12: false })}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { window.location.href = `/e/approval-requests/${item.id}`; }}>
                    Approve
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Sub-components ────────────────────────────

function MiniCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <p className="text-[10px] text-muted-foreground">{label}</p>
            <p className="text-xl font-bold">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
